import { PathParams, rest } from 'msw'
import { MultiFactorAuthorizationRequest } from 'types/mfa'
import {
  CreateDistributionRuleRequest,
  CreatePortfolioRequest,
  CustomerEarningsSummaryResponse,
  DepositRequest,
  DepositTransferResponse,
  DistributionRequest,
  EarningsDistributionResponse,
  GetMonthlyCustomersEarningsResponse,
  GetPortfoliosResponse,
  GetRewardsProductsResponse,
  Portfolio,
  PortfolioStatus,
  RewardsProduct,
  WithdrawalRequest,
  WithdrawalTransferResponse,
} from 'types/rewards'
import mockCreatePortfolio from '../../../__mocks__/Rewards/createPortfolioResponse.json'
import mockDepositToPortfolio from '../../../__mocks__/Rewards/depositToPortfolioResponse.json'
import mockGetCustomerEarningsSummary from '../../../__mocks__/Rewards/getCustomerEarningsSummaryResponse.json'
import mockGetMonthlyCustomersEarnings from '../../../__mocks__/Rewards/getMonthlyCustomersEarningsResponse.json'
import mockGetPortfolios from '../../../__mocks__/Rewards/getPortfoliosResponse.json'
import mockGetRewardsProducts from '../../../__mocks__/Rewards/getRewardsProductsResponse.json'
import mockMfaChallenge from '../../../__mocks__/Rewards/initiateCustomersRewardsWithdrawalMfaChallengeResponse.json'
import mockWithdrawFromPortfolio from '../../../__mocks__/Rewards/withdrawFromPortfolioResponse.json'

const API_URL = process.env.API_URL ?? ''

// Track portfolio poll counts for simulating async activation
const portfolioPollCounts = new Map<string, number>()

// Helper function to reset poll counts (useful for testing)
export const resetPortfolioPollCounts = () => {
  portfolioPollCounts.clear()
}

export const createPortfolio = rest.post<CreatePortfolioRequest, PathParams, Portfolio>(
  `${API_URL}/earn/v1/portfolios`,
  async (req, res, ctx) => {
    const body = await req.json<CreatePortfolioRequest>()

    const portfolioId = `portfolio-${Date.now()}`

    // Initialize poll count for this new portfolio
    portfolioPollCounts.set(portfolioId, 0)

    const response: Portfolio = {
      ...(mockCreatePortfolio as Portfolio),
      reference: body.reference,
      id: portfolioId,
      status: PortfolioStatus.CREATED,
    }

    return res(ctx.status(201), ctx.json(response))
  }
)

export const getPortfolios = rest.get<GetPortfoliosResponse>(
  `${API_URL}/earn/v1/portfolios`,
  async (req, res, ctx) => {
    return res(ctx.status(200), ctx.json(mockGetPortfolios))
  }
)

export const getPortfolio = rest.get<Portfolio>(
  `${API_URL}/earn/v1/portfolios/:portfolioId`,
  async (req, res, ctx) => {
    const { portfolioId } = req.params
    const id = portfolioId as string

    // Get current poll count for this portfolio
    const currentPollCount = portfolioPollCounts.get(id) ?? 0

    // Increment poll count
    portfolioPollCounts.set(id, currentPollCount + 1)

    // Determine status based on poll count
    // First 3 polls (0, 1, 2) return CREATED, 4th poll (3) onwards returns ACTIVE
    const status = currentPollCount < 3 ? PortfolioStatus.CREATED : PortfolioStatus.ACTIVE

    const response: Portfolio = {
      ...(mockCreatePortfolio as Portfolio),
      id,
      status,
    }

    return res(ctx.status(200), ctx.json(response))
  }
)

export const depositToPortfolio = rest.post<DepositRequest, PathParams, DepositTransferResponse>(
  `${API_URL}/earn/v1/deposits`,
  async (req, res, ctx) => {
    const body = await req.json<DepositRequest>()

    const response: DepositTransferResponse = {
      ...(mockDepositToPortfolio as DepositTransferResponse),
      reference: body.reference,
      portfolioId: body.portfolioId,
      originatorWalletId: body.originator.walletId,
      amount: body.originator.amount,
    }

    return res(ctx.status(201), ctx.json(response))
  }
)

export const withdrawFromPortfolio = rest.post<
  WithdrawalRequest,
  PathParams,
  WithdrawalTransferResponse
>(`${API_URL}/earn/v1/withdrawals`, async (req, res, ctx) => {
  const body = await req.json<WithdrawalRequest>()

  const response: WithdrawalTransferResponse = {
    ...(mockWithdrawFromPortfolio as WithdrawalTransferResponse),
    reference: body.reference,
    portfolioId: body.portfolioId,
    beneficiaryWalletId: body.beneficiary.walletId,
    amount: body.beneficiary.amount,
  }

  return res(ctx.status(201), ctx.json(response))
})

export const getRewardsProducts = rest.get<GetRewardsProductsResponse>(
  `${API_URL}/earn/v1/products`,
  async (req, res, ctx) => {
    const pageNumber = Number(req.url.searchParams.get('pageNumber')) || 0
    const pageSize = Number(req.url.searchParams.get('pageSize')) || 20
    const currency = req.url.searchParams.get('currency')

    let filteredProducts = [...(mockGetRewardsProducts as GetRewardsProductsResponse).content]

    if (currency) {
      filteredProducts = filteredProducts.filter(
        product => product.currency.toUpperCase() === currency.toUpperCase()
      )
    }

    const response: GetRewardsProductsResponse = {
      content: filteredProducts.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize),
      totalElements: filteredProducts.length,
      pageable: {
        pageNumber,
        pageSize,
      },
    }

    return res(ctx.status(200), ctx.json(response))
  }
)

export const getRewardsProduct = rest.get<RewardsProduct>(
  `${API_URL}/earn/v1/products/:rewardsProductId`,
  async (req, res, ctx) => {
    const { rewardsProductId } = req.params
    const id = rewardsProductId as string

    const allProducts = (mockGetRewardsProducts as GetRewardsProductsResponse).content
    const product = allProducts.find(p => p.id === id)

    if (!product) {
      return res(ctx.status(404), ctx.json({ error: 'Product not found' }))
    }

    return res(ctx.status(200), ctx.json(product))
  }
)

export const getCustomerEarningsSummary = rest.get<CustomerEarningsSummaryResponse>(
  `${API_URL}/earn/v1/portfolios/summary`,
  async (req, res, ctx) => {
    const q = req.url.searchParams.get('q')

    if (!q) {
      return res(
        ctx.status(400),
        ctx.json({
          timestamp: new Date().toISOString(),
          status: 400,
          error: 'Bad Request',
          message: 'Query parameter q is required',
          path: '/earn/v1/portfolios/summary',
        })
      )
    }

    const currencyMatch = q.match(/currency:(\w+)/)
    const currency = currencyMatch ? currencyMatch[1] : 'USDC'

    const response: CustomerEarningsSummaryResponse = {
      ...(mockGetCustomerEarningsSummary as CustomerEarningsSummaryResponse),
      currency,
    }

    return res(ctx.status(200), ctx.json(response))
  }
)

export const getMonthlyCustomersEarnings = rest.get<GetMonthlyCustomersEarningsResponse>(
  `${API_URL}/earn/v1/monthly-aggregates`,
  async (req, res, ctx) => {
    const currency = req.url.searchParams.get('currency')
    const startDate = req.url.searchParams.get('startDate')
    const endDate = req.url.searchParams.get('endDate')
    const pageNumber = Number(req.url.searchParams.get('pageNumber')) || 0
    const pageSize = Number(req.url.searchParams.get('pageSize')) || 20

    if (!currency) {
      return res(
        ctx.status(400),
        ctx.json({
          timestamp: new Date().toISOString(),
          status: 400,
          error: 'Bad Request',
          message: 'Currency parameter is required',
          path: '/earn/v1/monthly-aggregates',
        })
      )
    }

    let filteredEarnings = [
      ...(mockGetMonthlyCustomersEarnings as GetMonthlyCustomersEarningsResponse).content,
    ]

    // Filter by currency (case-insensitive)
    filteredEarnings = filteredEarnings.filter(
      earning => earning.currency.toUpperCase() === currency.toUpperCase()
    )

    // Filter by date range
    if (startDate) {
      filteredEarnings = filteredEarnings.filter(
        earning => new Date(earning.earningsPeriodEnd) >= new Date(startDate)
      )
    }

    if (endDate) {
      filteredEarnings = filteredEarnings.filter(
        earning => new Date(earning.earningsPeriodEnd) <= new Date(endDate)
      )
    }

    // Calculate pagination
    const totalElements = filteredEarnings.length
    const paginatedContent = filteredEarnings.slice(
      pageNumber * pageSize,
      (pageNumber + 1) * pageSize
    )

    const response: GetMonthlyCustomersEarningsResponse = {
      content: paginatedContent,
      totalElements,
      pageable: {
        pageNumber,
        pageSize,
      },
    }

    return res(ctx.status(200), ctx.json(response))
  }
)

export const initiateCustomersRewardsDistribution = rest.post(
  `${API_URL}/earn/v1/distributions`,
  async (req, res, ctx) => {
    const body = await req.json<DistributionRequest | MultiFactorAuthorizationRequest>()

    if ('challenge' in body && 'tokenResponse' in body) {
      const authRequest = body as MultiFactorAuthorizationRequest

      const expectedChallenge = 'abc123xyz'
      if (authRequest.challenge !== expectedChallenge) {
        return res(ctx.status(403))
      }

      if (
        !authRequest.tokenResponse ||
        authRequest.tokenResponse.length < 6 ||
        authRequest.tokenResponse !== '123456'
      ) {
        return res(ctx.status(403))
      }

      return res(ctx.status(201))
    }

    const distributionRequest = body as DistributionRequest

    if (!distributionRequest.destinationWalletId || !distributionRequest.reference) {
      return res(
        ctx.status(400),
        ctx.json({
          error: 'Bad Request',
          message: 'Missing required fields: destinationWalletId and reference',
          timestamp: new Date().toISOString(),
        })
      )
    }

    return res(
      ctx.status(428),
      ctx.json({
        ...mockMfaChallenge,
        callbackUrl: `${API_URL}/earn/v1/distributions`,
      })
    )
  }
)

export const getEarningsDistribution = rest.get<EarningsDistributionResponse>(
  `${API_URL}/earn/v1/distribution-rules`,
  async (req, res, ctx) => {
    const currency = req.url.searchParams.get('currency')

    if (!currency) {
      return res(
        ctx.status(400),
        ctx.json({
          timestamp: new Date().toISOString(),
          status: 400,
          error: 'Bad Request',
          message: 'Currency parameter is required',
          path: '/earn/v1/distribution-rules',
        })
      )
    }

    const response: EarningsDistributionResponse = {
      accountId: 'mock-account-id',
      currency,
      automatic: false,
    }

    return res(ctx.status(200), ctx.json(response))
  }
)

export const createDistributionRule = rest.post<CreateDistributionRuleRequest>(
  `${API_URL}/earn/v1/distribution-rules`,
  async (req, res, ctx) => {
    const body = await req.json<CreateDistributionRuleRequest>()

    if (!body.destinationWalletId || !body.reference) {
      return res(
        ctx.status(400),
        ctx.json({
          error: 'Bad Request',
          message: 'Missing required fields: destinationWalletId and reference',
          timestamp: new Date().toISOString(),
        })
      )
    }

    return res(ctx.status(201))
  }
)

export const authorizeWithdrawal = rest.post<MultiFactorAuthorizationRequest>(
  `${API_URL}/earn/v1/withdrawals-authorisations`,
  async (req, res, ctx) => {
    const body = await req.json<MultiFactorAuthorizationRequest>()

    const expectedChallenge = 'abc123xyz'
    if (body.challenge !== expectedChallenge) {
      return res(ctx.status(403), ctx.json({ error: 'Invalid challenge' }))
    }

    if (!body.tokenResponse || body.tokenResponse.length < 6 || body.tokenResponse !== '123456') {
      return res(ctx.status(403), ctx.json({ error: 'Invalid token response' }))
    }

    return res(ctx.status(201))
  }
)

const rewardsHandlers = [
  createPortfolio,
  getPortfolios,
  getCustomerEarningsSummary, // Must come before getPortfolio to match /portfolios/summary before /portfolios/:portfolioId
  getPortfolio,
  depositToPortfolio,
  withdrawFromPortfolio,
  getRewardsProducts,
  getRewardsProduct,
  getMonthlyCustomersEarnings,
  initiateCustomersRewardsDistribution,
  getEarningsDistribution,
  createDistributionRule,
  authorizeWithdrawal,
]

export default rewardsHandlers
